import { EntitiesStore } from './entities-store.abstract';

import { HttpClient } from '@angular/common/http';

import { Injector } from '@angular/core';

import { CountryDependencyEntityStore } from './country-dependency-entity-store';
import { CategoryCombo } from '../filters-store/filters-store';
import { environment } from '../../../../environments/environment';
import { tap } from 'rxjs/operators';
import { IAthlete } from './worldranking-store';
import { of } from 'rxjs';
import { IEvents } from './competition-store';

export interface IAthleteBiography {
  Id: number;
  FName: string;
  GName: string;
  FLocalizedName: string;
  GLocalizedName: string;
  WNameOrd: boolean;
  NOC: string;
  Gender: string;
  CountryName: string;
  CountryShort: string;
  ContinentalAssoc: string;
  DoB: Date;
  Age: number;
  HeaderImage: any[] | { URL: string, MimeType: string };
  WorldRankings: WorldRankings;
  Stats: any[];
  IndoorStats: any[];
  BioFields: BioField[];
  SocialMedia: SocialMedia[];
  AthleteCategories: IAthleteCategories[];
  Medals: any[];
  Caps: any[];
  Records: any[];
  Positions: Positions;
}

export interface IAthleteCategories {
  IndividualTeam: number;
  CatId: number;
  Cat: string;
  isTeam: boolean;
  Division: string[];
}

export interface BioField {
  BioId: number | string;
  Title: string;
  Value: string;
}

export interface Positions {
  COL: Col[];
  WA: Col[];
  WAAm: Col[];
}

export interface Col {
  // Type added when using in component
  type: string;
  FunCode: string;
  FunName: string;
  FunLevel: string;
  FunGroup: string;
  FunWaPosition: boolean;
  FunStart: string;
  FunEnd: string;
}

export interface SocialMedia {
  Title: string;
  Value: string;
}

export interface WorldRankings {
  Current: any[];
  Best: any[];
}


/**
 * The store for the official API of WorldArchery that brings the athlete biography.
 *
 * @export
 * @class AthleteBiographyStore
 * @extends {EntitiesStore<IAthleteBiography>}
 */
export class AthleteBiographyStore extends EntitiesStore<IAthleteBiography> {

  /**
   * The athlete id.
   *
   * @type {string}
   * @memberof AthleteBiographyStore
   */
  public athlete_id: string;

  /**
   * Creates an instance of AthleteBiographyStore.
   * @param {HttpClient} http the http client from Angular.
   * @param {Injector} injector the injector from Angular.
   * @param {*} athlete_id The athlete id to be used for dependencies.
   * @memberof AthleteBiographyStore
   */
  constructor(http: HttpClient, injector: Injector, athlete_id) {
    super('ATHLETEBIOGRAPHY', http, [
      { entity: new CountryDependencyEntityStore(http, injector), params: { 'RBP': 'ALL' } },
      { entity: new CategoryCombo('', '', http, []), params: { 'RBP': 'ALL' } },
      { entity: new AthleteOlympicsStore(http, injector, athlete_id), params: { 'Id': athlete_id } },
      { entity: new AthleteWCupStore(http, injector, athlete_id), params: { 'RBP': 'ALL', 'Id': athlete_id, 'SublevelId': 13 } }
    ]);
    this.athlete_id = athlete_id;

  }

  /**
   * Get the categories using the dependencies.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get categories() {
    return this.dependencies[1] && this.dependencies[1].entity && this.dependencies[1].entity.data.length > 0 ?
      this.dependencies[1].entity.data : [];
  }

  /**
   * Get the olympic information of the athlete for insights.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympics() {
    return this.dependencies[2] && this.dependencies[2].entity &&
      this.dependencies[2].entity.data.length > 0 ?
      this.dependencies[2].entity.data : [];
  }

  /**
   * A getter to check if the athlete participated for the logo on avatar.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympic_participant_logo() {
    return this.olympics.filter(value => value.SubLevelId === 1).filter(value => value.Caps > 0 && value.Podiums === 0);
  }
  /**
* A getter to check if the athlete participated for the logo on avatar.
*
* @readonly
* @memberof AthleteBiographyStore
*/
  get paralympic_participant_logo() {
    return this.olympics.filter(value => value.SubLevelId === 2).filter(value => value.Caps > 0 && value.Podiums === 0);
  }

  /**
   * A getter to check if the athlete participated.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympic_participant() {
    return this.olympics.filter(value => value.SubLevelId === 1);
  }

  /**
 * A getter to check if the athlete participated.
 *
 * @readonly
 * @memberof AthleteBiographyStore
 */
  get paralympic_participant() {
    return this.olympics.filter(value => value.SubLevelId === 2);
  }

  /**
   * Get the olympic champion insight. Item 2.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympic_champion() {
    return this.olympics ? this.olympics.filter(value => value.SubLevelId === 1)
      .filter(value => value.Details.filter(details => details.Individual.Rnk === 1).length > 0)
      : []
  }

  /**
   * Get the olympic medallist insight. Item 3.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympic_medallist() {
    return (this.olympics && this.olympic_champion.length == 0) ?
      this.olympics.filter(value => value.SubLevelId === 1)
        .filter(value => value.Medals.Silvers > 0 || value.Medals.Bronzes > 0 || value.Medals.Golds > 0) : []
  }

  /**
   * Get the paralympic champion insight. Item 4.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get paralympic_champion() {
    return this.olympics ? this.olympics.filter(value => value.SubLevelId === 2)
      .filter(value => value.Details.filter(details => details.Individual.Rnk === 1).length > 0) : [];
  }

  /**
   * Get the paralympic medallist insight. Item 5.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get paralympic_medallist() {
    return (this.olympics && this.paralympic_champion.length == 0) ?
      this.olympics.filter(value => value.SubLevelId === 2)
        .filter(value => value.Medals.Silvers > 0 || value.Medals.Bronzes > 0 || value.Medals.Golds > 0) : []
  }

  /**
   * Get the world archery championship insight. Item 6.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get world_archery_champion() {
    const wa_champion = this.data && this.data[0] ?
      Array.from(this.data[0].Medals
        .filter(value => value.Rnk === 1 && value.ComLevel === 2 && value.IndividualTeam === 1)
        .filter(value => ['4', '5', '6', '7', '8', '9', '10'].indexOf(value.ComSubLevel.toString()) > -1)
        .reduce((f, s) => f.set(s.ComSubLevelDescr, s), new Map()).values()).sort((f: any, s: any) => f.ComSubLevel > s.ComSubLevel ? 1 : -1)

      : [];
    return wa_champion;
  }

  /**
   * Get the olympian insight. Item 9.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympian() {
    if (this.olympic_champion.length > 0 || this.olympic_medallist.length > 0) {
      return [];
    } else {
      return this.olympics.filter(value => value.SubLevelId === 1).filter(value => value.Caps > 0).filter(value => value.Podiums === 0);
    }
  }

  /**
   * Get the olympian insight. Item 10.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */

  get paralympic() {
    if (this.paralympic_champion.length > 0 || this.paralympic_medallist.length > 0) {
      return [];
    } else {
      return this.olympics.filter(value => value.SubLevelId === 2).filter(value => value.Caps > 0).filter(value => value.Podiums === 0);
    }
  }


  /**
   * Get the world medallist insight. Item 11
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get world_medallist() {
    const medallist = this.data && this.data[0] ?
      this.data[0].Medals
        .filter(value => value.ComLevel.toString() === "2")
        .filter(value => ['4', '5', '6', '7', '8', '9', '10'].indexOf(value.ComSubLevel.toString()) > -1)
        .filter(value => value.Rnk === 1 || value.Rnk === 2 || value.Rnk === 3)
        .sort((f, s) => f.Rnk > s.Rnk ? 1 : -1)
      : [];
    const gold = medallist.filter(value => value.IsTeam.toString() === "1")

    return gold.length > 0 ? [gold[0]] : medallist.length > 0 ? [medallist.filter(value => value.Rnk != 1)[0]] : [];

  }

  /**
   * Get the olympic record holder. Item 12
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympic_record_holder() {
    const records = this.data &&
      this.data[0] ?
      this.data[0].Records.filter(record => record.RecordStanding === true && record.ParaAr === false && record.RecordLevelId === 1)
      : [];
    return records.length > 0 ? [records[0]] : [];
  }

  /**
   * Get the paralympic record holder. Item 13
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get paralympic_record_holder() {
    const records = this.data &&
      this.data[0] ?
      this.data[0].Records.filter(record => record.RecordStanding === true && record.ParaAr === true && record.RecordLevelId === 1)
      : [];
    return records.length > 0 ? [records[0]] : [];
  }

  /**
   * Get the world record holder. Item 14
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get world_record_holder() {
    const records = this.data &&
      this.data[0] ?
      this.data[0].Records.filter(record => record.RecordStanding === true && record.RecordLevelId === 2)
      : [];
    return records.length > 0 ? [records[0]] : [];
  }

  /**
   * Get the competition statistics insights.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get competition_stats() {
    return this.data &&
      this.data[0] ?
      Object.keys(this.data[0].Stats).map(year => this.data[0].Stats[year][0]).filter(Boolean)
      : [];
  }

  /**
   * Get the competition best statistics insight. Item 15.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */

  get competition_best_compound() {
    const competition_best = this.competition_stats.filter(stats => stats.DivCode === 2 && stats.QBest >= 680).sort((f, s) => f.QBest < s.QBest ? 1 : -1);
    return competition_best.length > 0 ? [competition_best[0]] : []
  }

  /**
   * Get the competition best statistics insight. Item 15.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get competition_best_recurve() {
    const competition_best = this.competition_stats.filter(stats => stats.DivCode === 1 && stats.QBest >= 700).sort((f, s) => f.QBest < s.QBest ? 1 : -1)
    return competition_best.length > 0 ? [competition_best[0]] : []
  }

  /**
   * Get the competition match wins statistics insight. Item 16.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get match_wins() {
    const matchBest = this.competition_stats.filter(stats => stats.MatchWinPercentage >= 60 && stats.MatchTot >= 50).sort((f, s) => f.MatchWinPercentage < s.MatchWinPercentage ? 1 : -1);
    return matchBest.length > 0 ? [matchBest[0]] : [];
  }

  /**
   * Get the WCup Athlete insights. Item 8.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get world_cup_champion() {
    const wCupData = this.dependencies[3] && this.dependencies[3].entity &&
      this.dependencies[3].entity.data.length > 0 ?
      this.dependencies[3].entity.data : [];
    const filteredData = [].concat.apply([], wCupData.length > 0 ?
      wCupData.filter(value => value.SubLevelId.toString() === "13")
        .map(data => data.Details) : [])

      .filter(wCupDetails => wCupDetails.Individual.Rnk === 1);
    return filteredData.length > 0 ? { count: filteredData.length } : undefined;
  }

  /**
   * Getter for get the data for olympic panel in biography.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get olympic_panel(){
    return this.olympics.filter(value=> value.SubLevelId.toString() === '1');
  }

  /**
   * Getter for get the data for paralympic panel in biography.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get paralympic_panel(){
    return this.olympics.filter(value=> value.SubLevelId.toString() === '2');
  }

  /**
   * Getter for get the data for youth olympic panel in biography.
   *
   * @readonly
   * @memberof AthleteBiographyStore
   */
  get youth_olympic_panel(){
    return this.olympics.filter(value=> value.SubLevelId.toString() === '3');
  }
}


/**
 * Store class foe get the athlete olympics results (Used for get the insights.)
 *
 * @export
 * @class AthleteOlympicsStore
 * @extends {EntitiesStore<IAthleteBiography>}
 */
export class AthleteOlympicsStore extends EntitiesStore<IAthleteBiography> {

  /**
   * The athlete id.
   *
   * @type {string}
   * @memberof AthleteOlympicsStore
   */
  public athlete_id: string;
  /**
 * Creates an instance of AthleteBiographyStore.
 * @param {HttpClient} http the http client from Angular.
 * @param {Injector} injector the injector from Angular.
 * @param {*} athlete_id The athlete id to be used for dependencies.
 * @memberof AthleteOlympicsStore
 */
  constructor(http: HttpClient, injector: Injector, athlete_id) {
    super('ATHLETEOLYMPICS', http, [
      { entity: new CountryDependencyEntityStore(http, injector), params: { 'RBP': 'ALL' } },
      { entity: new CategoryCombo('', '', http, []), params: { 'RBP': 'ALL' } }]);
    this.athlete_id = athlete_id;

  }

}

/**
 * The athlete World Cup Ranking store for get the insights.
 *
 * @export
 * @class AthleteWCupStore
 * @extends {EntitiesStore<IAthleteBiography>}
 */
export class AthleteWCupStore extends EntitiesStore<IAthleteBiography> {
  /**
  * The athlete id.
  *
  * @type {string}
  * @memberof AthleteWCupStore
  */
  public athlete_id: string;
  /**
  * Creates an instance of AthleteBiographyStore.
  * @param {HttpClient} http the http client from Angular.
  * @param {Injector} injector the injector from Angular.
  * @param {*} athlete_id The athlete id to be used for dependencies.
  * @memberof AthleteWCupStore
  */
  constructor(http: HttpClient, injector: Injector, athlete_id) {
    super('ATHLETEWCUPS', http, [
      { entity: new CountryDependencyEntityStore(http, injector), params: { 'RBP': 'ALL' } },
      { entity: new CategoryCombo('', '', http, []), params: { 'RBP': 'ALL' } }
    ]);
    this.athlete_id = athlete_id;

  }

}

/**
 * The editorial competition object of the athlete biography.
 *
 * @export
 * @interface IEditorialCompetition
 */
export interface IEditorialCompetition {
  name: string;
  id: number;
  place: string;
  country: string;
  date_start: string;
  date_end: string;
}

/**
 * The editorial biography interface for the athlete.
 *
 * @export
 * @interface IAthleteEditorialBiography
 */
export interface IAthleteEditorialBiography {
  // athlete: IAthlete;
  // competition: IEditorialCompetition;
  author: string;
  date: string;
  body: string;
  publish_date: string;
  athletes:IAthlete[];
  competitions:{date_start:string, name:string}[];
}


const mockEditorialData = [
  {/*
    "athlete": {
      "GName": "Woojin",
      "FName": "Kim",
      "WNameOrd": true,
      "id": '8179'
    },
    "competition": {
      "name": "Shanghai 2017 Hyundai Archery World Cup Stage 1",
      "id": '15914',
      "place": "Shanghai",
      "country": "CHN",
      "date_start": "2017-05-15",
      "date_end": "2017-05-20"
    },*/
    "author": "World Archery",
    "date": "2021-03-17 08:00:00",
    "body": "  <div class=\"wide-normal paragraph paragraph--type--wysiwyg paragraph--view-mode--default\">\n          \n            <div class=\"wysiwyg__field-body\"><p><em>French version below/Version française ci-dessous.</em></p>\n\n<p><a href=\"https://documents.worldarchery.org/Federation/Newsletter_Info/English/03_Mar2021-e.pdf\">The March 2021 edition of the World Archery newsletter is now available to read online.</a></p>\n\n<p>Highlights:</p>\n\n<ul><li>Changes to the event calendar including the relocations of Shanghai and Perth.</li>\n\t<li>Numerous member federation election results and two new members (Jordan and Kenya).</li>\n\t<li>Notification of athlete and official eLearning tools ahead of Tokyo 2020.</li>\n</ul><p><em>Submissions of content for the newsletter or website should be sent to <a href=\"mailto:info@archery.sport?subject=Newsletter%20content\">info@archery.sport</a>. Next issue deadline: <strong>19 April 2021</strong>.</em></p>\n\n<p>***</p>\n\n<p><a href=\"https://documents.worldarchery.org/Federation/Newsletter_Info/French/03_Mar2021-f.pdf\">L’édition du bulletin de mars 2021 est maintenant disponible en ligne</a>.</p>\n\n<p>Faits marquants:</p>\n\n<ul><li>Changements dans le calendrier des événements, notamment Shanghai et de Perth.</li>\n\t<li>Résultats des élections de fédérations membres et affiliation de deux nouveaux membres (Jordanie et Kenya).</li>\n\t<li>Outils d’apprentissage en ligne pour les athlètes et officiels en vue de Tokyo 2020.</li>\n</ul><p><em>Il est possible de soumettre du contenu pour ce bulletin ou le site de World Archery en écrivant à <a href=\"mailto:info@archery.sport?subject=Newsletter%20content\">info@archery.sport</a>. Délai pour le prochain numéro: <strong>19 avril 2021</strong>.</em></p></div>\n      \n      </div>\n  <div class=\"paragraph paragraph--type--documents-links paragraph--view-mode--default\">\n          \n            <div class=\"documents-links__field-title\"><h2>Documents and links</h2>\n</div>\n      \n      <div class=\"documents-links__field-paragraphs\">\n              <div class=\"field__item\">  <div class=\"paragraph paragraph--type--document paragraph--view-mode--default\">\n          <div class=\"document__field-documents\"><a href=\"https://documents.worldarchery.sport/?doc=5338\">2021 – 03 March EN – English (.pdf)</a></div>\n\n      </div>\n</div>\n              <div class=\"field__item\">  <div class=\"paragraph paragraph--type--document paragraph--view-mode--default\">\n          <div class=\"document__field-documents\"><a href=\"https://documents.worldarchery.sport/?doc=5339\">2021 – 03 Mars FR – French (.pdf)</a></div>\n\n      </div>\n</div>\n          </div>\n  \n      </div>\n"
  }
]


/**
 * The store for the Drupal API that contains WYSIWYG information about the athlete.
 *
 * @export
 * @class AthleteEditorialBiographyStore
 * @extends {EntitiesStore<IAthleteEditorialBiography>}
 */
export class AthleteEditorialBiographyStore extends EntitiesStore<IAthleteEditorialBiography> {
  constructor(http: HttpClient, injector: Injector, public athleteId: string) {
    super(null, http, [{ entity: new CountryDependencyEntityStore(http, injector), params: { 'RBP': 'ALL' } }]);
  }

  /**
   * {@inheritdoc}
   *
   * @memberof AthleteEditorialBiographyStore
   */
  getRequest(params: any, request_more: boolean) {
    // tslint:disable-next-line:max-line-length
    return this.http.get<IAthleteEditorialBiography[]>(environment.athlete_biography.replace('athlete_id', this.athleteId.toString()))
      .pipe(
        tap((a: any) => {
          this.data = a;
          this.dataStream.next(this.data);
        })
      ) as any;
  }
}
